import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckIcon, 
  XMarkIcon, 
  Bars3Icon, 
  ArrowRightIcon,
  BoltIcon,
  ChartBarIcon,
  CpuChipIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  RocketLaunchIcon,
  StarIcon,
  PlayCircleIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  MegaphoneIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  // Video modal can be implemented with a portal or modal component
  const handleWatchDemo = () => {
    // For now, navigate to a demo or open video in new tab
    window.open('https://www.youtube.com/watch?v=demo', '_blank');
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <img src="/logo.jpg" alt="Marketeer" className="logo-img" />
            <span className="logo-text">Marketeer</span>
          </div>
          
          <div className="nav-links desktop-only">
            <button onClick={() => scrollToSection('features')}>Features</button>
            <button onClick={() => scrollToSection('how-it-works')}>How it Works</button>
            <button onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button onClick={() => scrollToSection('faq')}>FAQ</button>
          </div>

          <div className="nav-actions desktop-only">
            <button className="btn-text" onClick={() => navigate('/login')}>Log in</button>
            <button className="btn-primary" onClick={() => navigate('/register')}>Get Started</button>
          </div>

          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <XMarkIcon /> : <Bars3Icon />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <button onClick={() => scrollToSection('features')}>Features</button>
            <button onClick={() => scrollToSection('how-it-works')}>How it Works</button>
            <button onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button onClick={() => scrollToSection('faq')}>FAQ</button>
            <div className="mobile-actions">
              <button className="btn-text" onClick={() => navigate('/login')}>Log in</button>
              <button className="btn-primary" onClick={() => navigate('/register')}>Get Started</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <header className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <span>New</span> AI Marketing Crew 2.0 is here
          </div>
          <h1 className="hero-title">
            Stop Guessing. <br />
            <span className="gradient-text">Start Dominating.</span>
          </h1>
          <p className="hero-subtitle">
            The all-in-one AI marketing platform that turns data into revenue. 
            Automate your strategy, content, and campaigns with a crew of AI agents working 24/7.
          </p>
          <div className="hero-cta">
            <button className="btn-primary btn-lg" onClick={() => navigate('/register')}>
              Start Free Trial <ArrowRightIcon className="icon-sm" />
            </button>
            <button className="btn-secondary btn-lg" onClick={() => navigate('/login')}>
              Live Demo
            </button>
          </div>
          <div className="hero-trust">
            <p>Trusted by forward-thinking marketing teams</p>
            <div className="trust-logos">
              {['Acme Corp', 'GlobalTech', 'Nebula', 'FoxRun', 'Circle'].map((logo, i) => (
                <span key={i} className="trust-logo">{logo}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <button className="video-play-btn" onClick={handleWatchDemo}>
            <PlayCircleIcon />
            <span>Watch Demo</span>
          </button>
          <div className="dashboard-preview">
            <div className="preview-header">
              <div className="preview-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
            <div className="preview-body">
              <div className="preview-sidebar"></div>
              <div className="preview-main">
                <div className="preview-chart"></div>
                <div className="preview-grid">
                  <div></div><div></div><div></div>
                </div>
              </div>
            </div>
            {/* Floating Elements */}
            <div className="float-card card-1">
              <div className="float-icon"><BoltIcon /></div>
              <div className="float-text">
                <span>ROI Increased</span>
                <strong>+127%</strong>
              </div>
            </div>
            <div className="float-card card-2">
              <div className="float-icon"><CpuChipIcon /></div>
              <div className="float-text">
                <span>AI Agents</span>
                <strong>Active</strong>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <section className="stats-bar">
        <div className="stats-container">
          {[
            { value: '10,000+', label: 'Active Users', icon: <UserGroupIcon /> },
            { value: '5M+', label: 'Campaigns Launched', icon: <RocketLaunchIcon /> },
            { value: '127%', label: 'Avg. ROI Increase', icon: <ChartBarIcon /> },
            { value: '4.9/5', label: 'Customer Rating', icon: <StarIcon /> }
          ].map((stat, idx) => (
            <div key={idx} className="stat-item">
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="problem-section">
        <div className="section-container">
          <div className="problem-grid">
            <div className="problem-content">
              <h2 className="section-title problem-title">Marketing is broken.</h2>
              <p className="section-desc">
                You're drowning in tools, data is siloed, and your team is burnt out creating content that doesn't convert. 
                The old way of marketing is slow, expensive, and unpredictable.
              </p>
              <ul className="problem-list">
                <li><XMarkIcon /> Disconnected tools & data silos</li>
                <li><XMarkIcon /> Manual, repetitive tasks</li>
                <li><XMarkIcon /> Guesswork instead of data-driven strategy</li>
              </ul>
            </div>
            <div className="solution-content">
              <h2 className="section-title">Marketeer is the fix.</h2>
              <p className="section-desc">
                Unify your entire marketing stack into one intelligent platform. 
                Let AI handle the heavy lifting while you focus on strategy and creative direction.
              </p>
              <ul className="solution-list">
                <li><CheckIcon /> All-in-one integrated platform</li>
                <li><CheckIcon /> AI-powered automation & creation</li>
                <li><CheckIcon /> Predictive analytics & insights</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Agency / Multi-Company Section */}
      <section className="agency-section">
        <div className="agency-container">
          <div className="agency-content">
            <div className="agency-badge">For Agencies & Enterprises</div>
            <h2 className="section-title">One Platform.<br /><span className="gradient-text">Unlimited Companies.</span></h2>
            <p className="section-desc">
              Whether you're an agency managing multiple client accounts or an enterprise with several brands, 
              Marketeer's multi-tenant architecture gives you complete control from a single dashboard.
            </p>
            <div className="agency-features">
              <div className="agency-feature">
                <CheckIcon />
                <div>
                  <strong>Agency Dashboard</strong>
                  <span>Bird's-eye view of all client accounts, campaigns, and performance metrics</span>
                </div>
              </div>
              <div className="agency-feature">
                <CheckIcon />
                <div>
                  <strong>Client Workspaces</strong>
                  <span>Isolated environments for each company with custom branding and permissions</span>
                </div>
              </div>
              <div className="agency-feature">
                <CheckIcon />
                <div>
                  <strong>Team Roles & Access</strong>
                  <span>Granular permissions - assign team members to specific companies or projects</span>
                </div>
              </div>
              <div className="agency-feature">
                <CheckIcon />
                <div>
                  <strong>White-Label Reports</strong>
                  <span>Generate branded reports for clients with your agency's logo and colors</span>
                </div>
              </div>
              <div className="agency-feature">
                <CheckIcon />
                <div>
                  <strong>Centralized Billing</strong>
                  <span>One invoice, multiple companies - simplify your accounting</span>
                </div>
              </div>
              <div className="agency-feature">
                <CheckIcon />
                <div>
                  <strong>Cross-Company Analytics</strong>
                  <span>Compare performance across all your brands in one unified view</span>
                </div>
              </div>
            </div>
          </div>
          <div className="agency-visual">
            <div className="agency-diagram">
              <div className="diagram-header">
                <div className="diagram-icon">👔</div>
                <span>Agency / Enterprise HQ</span>
              </div>
              <div className="diagram-branches">
                <div className="diagram-branch">
                  <div className="branch-line"></div>
                  <div className="branch-company">
                    <span className="company-icon">🏢</span>
                    <span>Company A</span>
                    <span className="company-stats">12 Projects • 45 Campaigns</span>
                  </div>
                </div>
                <div className="diagram-branch">
                  <div className="branch-line"></div>
                  <div className="branch-company">
                    <span className="company-icon">🏪</span>
                    <span>Company B</span>
                    <span className="company-stats">8 Projects • 32 Campaigns</span>
                  </div>
                </div>
                <div className="diagram-branch">
                  <div className="branch-line"></div>
                  <div className="branch-company">
                    <span className="company-icon">🏭</span>
                    <span>Company C</span>
                    <span className="company-stats">5 Projects • 18 Campaigns</span>
                  </div>
                </div>
                <div className="diagram-branch">
                  <div className="branch-line"></div>
                  <div className="branch-company more">
                    <span className="company-icon">+</span>
                    <span>Add More...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="features-section">
        <div className="section-header">
          <h2 className="section-title">Everything you need to scale</h2>
          <p className="section-subtitle">Powerful tools for every stage of your marketing funnel</p>
        </div>
        <div className="features-grid">
          {[
            {
              icon: <CpuChipIcon />,
              title: "AI Marketing Crew",
              desc: "Deploy autonomous AI agents for research, content creation, and optimization."
            },
            {
              icon: <ChartBarIcon />,
              title: "Predictive Analytics",
              desc: "Know what will convert before you launch with advanced data modeling."
            },
            {
              icon: <GlobeAltIcon />,
              title: "Multi-Channel Campaigns",
              desc: "Launch coordinated campaigns across Email, Social, Ads, and SMS instantly."
            },
            {
              icon: <BoltIcon />,
              title: "Content Engine",
              desc: "Generate SEO-optimized blogs, landing pages, and ad copy in seconds."
            },
            {
              icon: <UserGroupIcon />,
              title: "VIP & Loyalty",
              desc: "Build lasting relationships with integrated tier-based loyalty programs."
            },
            {
              icon: <ShieldCheckIcon />,
              title: "Enterprise Security",
              desc: "Bank-grade security, role-based access, and full audit logging."
            },
            {
              icon: <EnvelopeIcon />,
              title: "Email Marketing",
              desc: "AI-powered sequences with smart segmentation and A/B testing built-in."
            },
            {
              icon: <ChatBubbleLeftRightIcon />,
              title: "SMS & Chat",
              desc: "Reach customers instantly with automated conversational campaigns."
            },
            {
              icon: <MegaphoneIcon />,
              title: "Paid Ads Manager",
              desc: "Optimize Google, Meta, and TikTok ads with AI-driven bid management."
            }
          ].map((feature, idx) => (
            <div key={idx} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="steps-section">
        <div className="section-header">
          <h2 className="section-title">From Idea to Revenue in Minutes</h2>
        </div>
        <div className="steps-container">
          <div className="step-item">
            <div className="step-number">01</div>
            <h3>Connect Your Data</h3>
            <p>Integrate your existing tools and data sources in a few clicks.</p>
          </div>
          <div className="step-connector"></div>
          <div className="step-item">
            <div className="step-number">02</div>
            <h3>AI Analysis</h3>
            <p>Our AI agents analyze your market, competitors, and audience.</p>
          </div>
          <div className="step-connector"></div>
          <div className="step-item">
            <div className="step-number">03</div>
            <h3>Launch & Scale</h3>
            <p>Deploy optimized campaigns and watch your revenue grow.</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="section-header">
          <h2 className="section-title">Loved by Marketing Teams</h2>
          <p className="section-subtitle">See what our customers have to say</p>
        </div>
        <div className="testimonials-grid">
          {[
            {
              quote: "Marketeer replaced 5 different tools for us. Our team saves 20+ hours per week and our campaigns convert 3x better.",
              author: "Sarah Chen",
              role: "Head of Marketing",
              company: "TechFlow Inc.",
              avatar: "SC"
            },
            {
              quote: "The AI Marketing Crew is like having a team of expert strategists working 24/7. We've seen a 156% increase in qualified leads.",
              author: "Marcus Johnson",
              role: "CMO",
              company: "GrowthScale",
              avatar: "MJ"
            },
            {
              quote: "Finally, a platform that actually delivers on its AI promises. The predictive analytics alone paid for our annual subscription in the first month.",
              author: "Emily Rodriguez",
              role: "Marketing Director",
              company: "Nexus Digital",
              avatar: "ER"
            }
          ].map((testimonial, idx) => (
            <div key={idx} className="testimonial-card">
              <div className="testimonial-stars">
                {[1,2,3,4,5].map(s => <StarSolid key={s} />)}
              </div>
              <p className="testimonial-quote">"{testimonial.quote}"</p>
              <div className="testimonial-author">
                <div className="author-avatar">{testimonial.avatar}</div>
                <div className="author-info">
                  <strong>{testimonial.author}</strong>
                  <span>{testimonial.role}, {testimonial.company}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Section */}
      <section className="comparison-section">
        <div className="section-header">
          <h2 className="section-title">Why Choose Marketeer?</h2>
          <p className="section-subtitle">See how we stack up against the competition</p>
        </div>
        <div className="comparison-table-wrapper">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th className="highlight">Marketeer</th>
                <th>HubSpot</th>
                <th>Mailchimp</th>
                <th>Hootsuite</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>AI Content Generation</td>
                <td className="highlight"><CheckIcon className="check" /></td>
                <td><span className="partial">Limited</span></td>
                <td><XMarkIcon className="cross" /></td>
                <td><XMarkIcon className="cross" /></td>
              </tr>
              <tr>
                <td>Autonomous AI Agents</td>
                <td className="highlight"><CheckIcon className="check" /></td>
                <td><XMarkIcon className="cross" /></td>
                <td><XMarkIcon className="cross" /></td>
                <td><XMarkIcon className="cross" /></td>
              </tr>
              <tr>
                <td>Multi-Channel Campaigns</td>
                <td className="highlight"><CheckIcon className="check" /></td>
                <td><CheckIcon className="check" /></td>
                <td><span className="partial">Email Only</span></td>
                <td><span className="partial">Social Only</span></td>
              </tr>
              <tr>
                <td>Predictive Analytics</td>
                <td className="highlight"><CheckIcon className="check" /></td>
                <td><span className="partial">Basic</span></td>
                <td><XMarkIcon className="cross" /></td>
                <td><XMarkIcon className="cross" /></td>
              </tr>
              <tr>
                <td>Starting Price</td>
                <td className="highlight"><strong>$0/mo</strong></td>
                <td>$45/mo</td>
                <td>$13/mo</td>
                <td>$99/mo</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="section-header">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle">Choose the plan that fits your growth stage</p>
        </div>
        
        {/* Pricing Cards */}
        <div className="pricing-grid">
          {/* Free Tier */}
          <div className="pricing-card">
            <div className="card-header">
              <h3>Free</h3>
              <div className="price">$0<span>/mo</span></div>
              <p>Perfect for individuals just starting out</p>
            </div>
            <div className="card-features">
              <ul>
                <li><CheckIcon /> 1 User Seat</li>
                <li><CheckIcon /> 1 Company</li>
                <li><CheckIcon /> 1 Project</li>
                <li><CheckIcon /> 5 AI Generations/mo</li>
                <li><CheckIcon /> Basic Analytics</li>
                <li className="disabled"><XMarkIcon /> Multi-Company Support</li>
              </ul>
            </div>
            <button className="btn-outline" onClick={() => navigate('/register')}>Get Started</button>
          </div>

          {/* Basic Tier */}
          <div className="pricing-card">
            <div className="card-header">
              <h3>Basic</h3>
              <div className="price">$29<span>/mo</span></div>
              <p>For small teams and startups</p>
            </div>
            <div className="card-features">
              <ul>
                <li><CheckIcon /> 3 User Seats</li>
                <li><CheckIcon /> 1 Company</li>
                <li><CheckIcon /> 5 Projects</li>
                <li><CheckIcon /> 50 AI Generations/mo</li>
                <li><CheckIcon /> Email Marketing</li>
                <li className="disabled"><XMarkIcon /> Multi-Company Support</li>
              </ul>
            </div>
            <button className="btn-primary" onClick={() => navigate('/register')}>Start Trial</button>
          </div>

          {/* Pro Tier */}
          <div className="pricing-card popular">
            <div className="popular-badge">Most Popular</div>
            <div className="card-header">
              <h3>Pro</h3>
              <div className="price">$99<span>/mo</span></div>
              <p>For growing agencies</p>
            </div>
            <div className="card-features">
              <ul>
                <li><CheckIcon /> 10 User Seats</li>
                <li><CheckIcon /> <strong>5 Companies</strong></li>
                <li><CheckIcon /> Unlimited Projects</li>
                <li><CheckIcon /> Unlimited AI</li>
                <li><CheckIcon /> Agency Dashboard</li>
                <li><CheckIcon /> White-Label Reports</li>
              </ul>
            </div>
            <button className="btn-primary" onClick={() => navigate('/register')}>Start Trial</button>
          </div>

          {/* Enterprise Tier */}
          <div className="pricing-card">
            <div className="card-header">
              <h3>Enterprise</h3>
              <div className="price">Custom</div>
              <p>For large agencies & enterprises</p>
            </div>
            <div className="card-features">
              <ul>
                <li><CheckIcon /> Unlimited Users</li>
                <li><CheckIcon /> <strong>Unlimited Companies</strong></li>
                <li><CheckIcon /> Custom AI Training</li>
                <li><CheckIcon /> SSO & Security</li>
                <li><CheckIcon /> Dedicated Manager</li>
                <li><CheckIcon /> SLA Guarantee</li>
              </ul>
            </div>
            <button className="btn-outline" onClick={() => navigate('/contact')}>Contact Sales</button>
          </div>
        </div>

        {/* Extensive Feature Matrix */}
        <div className="feature-matrix">
          <h3 className="matrix-title">Complete Feature Comparison</h3>
          <div className="matrix-wrapper">
            <table className="matrix-table">
              <thead>
                <tr>
                  <th className="feature-col">Feature</th>
                  <th>Free</th>
                  <th>Basic</th>
                  <th className="highlight">Pro</th>
                  <th>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {/* Organization */}
                <tr className="category-row"><td colSpan="5">Organization & Access</td></tr>
                <tr><td>User Seats</td><td>1</td><td>3</td><td className="highlight">10</td><td>Unlimited</td></tr>
                <tr><td>Companies / Clients</td><td>1</td><td>1</td><td className="highlight">5</td><td>Unlimited</td></tr>
                <tr><td>Projects per Company</td><td>1</td><td>5</td><td className="highlight">Unlimited</td><td>Unlimited</td></tr>
                <tr><td>Agency Dashboard</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Role-Based Access Control</td><td><XMarkIcon className="cross" /></td><td>Basic</td><td className="highlight">Advanced</td><td>Custom</td></tr>
                <tr><td>Audit Logs</td><td><XMarkIcon className="cross" /></td><td>30 days</td><td className="highlight">1 year</td><td>Unlimited</td></tr>
                
                {/* AI Features */}
                <tr className="category-row"><td colSpan="5">AI & Automation</td></tr>
                <tr><td>AI Content Generations</td><td>5/mo</td><td>50/mo</td><td className="highlight">Unlimited</td><td>Unlimited</td></tr>
                <tr><td>AI Marketing Crew (Agents)</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Blog Automation</td><td><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Landing Page Generator</td><td><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Automation Rules</td><td><XMarkIcon className="cross" /></td><td>5 rules</td><td className="highlight">Unlimited</td><td>Unlimited</td></tr>
                <tr><td>Custom AI Model Training</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td></tr>
                
                {/* Marketing Channels */}
                <tr className="category-row"><td colSpan="5">Marketing Channels</td></tr>
                <tr><td>Email Marketing</td><td><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>SMS Marketing</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Social Media Management</td><td><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Paid Ads Manager</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Event Marketing</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Chat / Conversational</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                
                {/* Analytics & Insights */}
                <tr className="category-row"><td colSpan="5">Analytics & Insights</td></tr>
                <tr><td>Basic Analytics</td><td><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Predictive Analytics</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>SEO Audit Tools</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Competitor Analysis</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>A/B Testing</td><td><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Cross-Company Reports</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                
                {/* E-commerce & CRM */}
                <tr className="category-row"><td colSpan="5">E-commerce & CRM</td></tr>
                <tr><td>Lead Management</td><td><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Customer Database</td><td>100</td><td>1,000</td><td className="highlight">10,000</td><td>Unlimited</td></tr>
                <tr><td>VIP / Loyalty Program</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>E-commerce Analytics</td><td><XMarkIcon className="cross" /></td><td>Basic</td><td className="highlight">Advanced</td><td>Enterprise</td></tr>
                <tr><td>Marketplace Integrations</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Sales Signals / Radar</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                
                {/* Content Management */}
                <tr className="category-row"><td colSpan="5">Content & Assets</td></tr>
                <tr><td>Content Calendar</td><td><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Media Library Storage</td><td>100 MB</td><td>1 GB</td><td className="highlight">10 GB</td><td>Unlimited</td></tr>
                <tr><td>Document Library</td><td><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Content Templates</td><td>5</td><td>25</td><td className="highlight">Unlimited</td><td>Unlimited</td></tr>
                <tr><td>White-Label Reports</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                
                {/* Integrations */}
                <tr className="category-row"><td colSpan="5">Integrations & Security</td></tr>
                <tr><td>Native Integrations</td><td>5</td><td>25</td><td className="highlight">100+</td><td>100+</td></tr>
                <tr><td>API Access</td><td><XMarkIcon className="cross" /></td><td>Read-only</td><td className="highlight">Full</td><td>Full</td></tr>
                <tr><td>Webhooks</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>SSO / SAML</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Custom Integrations</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td></tr>
                
                {/* Support */}
                <tr className="category-row"><td colSpan="5">Support & SLA</td></tr>
                <tr><td>Community Support</td><td><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Email Support</td><td><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Priority Support</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><CheckIcon className="check" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>Dedicated Account Manager</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight"><XMarkIcon className="cross" /></td><td><CheckIcon className="check" /></td></tr>
                <tr><td>SLA Guarantee</td><td><XMarkIcon className="cross" /></td><td><XMarkIcon className="cross" /></td><td className="highlight">99.5%</td><td>99.9%</td></tr>
                <tr><td>Onboarding & Training</td><td>Self-serve</td><td>Docs</td><td className="highlight">Webinar</td><td>Dedicated</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="section-container">
          <h2 className="section-title text-center">Frequently Asked Questions</h2>
          <div className="faq-accordion">
            {[
              { q: "Can I cancel anytime?", a: "Yes, you can cancel your subscription at any time. No long-term contracts required for standard plans. We believe in earning your business every month." },
              { q: "Is there a free trial?", a: "Yes! Basic and Pro plans come with a 14-day free trial so you can explore the platform risk-free. No credit card required to start." },
              { q: "Do I need technical skills?", a: "Not at all. Marketeer is designed to be user-friendly and intuitive for marketers of all skill levels. Our AI handles the technical complexity." },
              { q: "How does the AI work?", a: "We use a combination of advanced LLMs (GPT-4, Claude) and proprietary models trained on high-performing marketing data. Our AI Marketing Crew operates autonomously to research, create, and optimize your campaigns." },
              { q: "Can I migrate from my current tools?", a: "Absolutely. We offer free migration assistance and can import your data from HubSpot, Mailchimp, Salesforce, and 50+ other platforms." },
              { q: "Is my data secure?", a: "Yes. We use bank-grade encryption, SOC 2 Type II compliance, and never train our AI models on your proprietary data. Your data stays yours." },
              { q: "What kind of support do you offer?", a: "Free plan includes community support. Basic gets email support. Pro and Enterprise get priority support with dedicated account managers and 24/7 live chat." },
              { q: "Can I integrate with my existing tools?", a: "Yes! We have native integrations with 100+ tools including Salesforce, Shopify, Stripe, Google Analytics, Meta Ads, and more. Plus a powerful API for custom integrations." }
            ].map((item, i) => (
              <div key={i} className={`faq-item ${activeFaq === i ? 'active' : ''}`}>
                <button className="faq-question" onClick={() => setActiveFaq(activeFaq === i ? null : i)}>
                  <span>{item.q}</span>
                  <span className="faq-toggle">{activeFaq === i ? '−' : '+'}</span>
                </button>
                <div className="faq-answer">
                  <p>{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="guarantee-section">
        <div className="guarantee-content">
          <div className="guarantee-icon">
            <ShieldCheckIcon />
          </div>
          <h2>30-Day Money-Back Guarantee</h2>
          <p>Try Marketeer risk-free. If you're not completely satisfied within the first 30 days, we'll refund 100% of your payment. No questions asked.</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to transform your marketing?</h2>
          <p>Join thousands of marketers using Marketeer to grow faster.</p>
          <div className="cta-buttons">
            <button className="btn-white btn-lg" onClick={() => navigate('/register')}>Get Started for Free</button>
            <p className="micro-copy">No credit card required • Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-col">
            <div className="nav-logo">
              <img src="/logo.jpg" alt="Marketeer" className="logo-img" />
              <span className="logo-text">Marketeer</span>
            </div>
            <p>The AI-powered marketing platform for agencies and businesses managing multiple brands.</p>
          </div>
          <div className="footer-col">
            <h4>Product</h4>
            <button onClick={() => scrollToSection('features')}>Features</button>
            <button onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button>Enterprise</button>
            <button>Roadmap</button>
          </div>
          <div className="footer-col">
            <h4>Company</h4>
            <button>About</button>
            <button>Careers</button>
            <button>Blog</button>
            <button>Contact</button>
          </div>
          <div className="footer-col">
            <h4>Legal</h4>
            <button>Privacy</button>
            <button>Terms</button>
            <button>Security</button>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 Marketeer Inc. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        :root {
          --primary: #6366f1;
          --primary-dark: #4f46e5;
          --secondary: #ec4899;
          --dark-bg: #0f172a;
          --dark-card: #1e293b;
          --dark-card-hover: #273548;
          --text-light: #f8fafc;
          --text-gray: #94a3b8;
          --gradient-main: linear-gradient(135deg, #6366f1 0%, #ec4899 100%);
          --gradient-subtle: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%);
          --max-width: 1560px;
          --success: #10b981;
          --warning: #f59e0b;
        }

        .landing-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background-color: var(--dark-bg);
          color: var(--text-light);
          min-height: 100vh;
          overflow-x: hidden;
          line-height: 1.6;
        }

        /* Buttons */
        .btn-primary {
          background: var(--primary);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .btn-primary:hover {
          background: var(--primary-dark);
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(99, 102, 241, 0.3);
        }
        .btn-secondary {
          background: rgba(255,255,255,0.1);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          border: 1px solid rgba(255,255,255,0.2);
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-secondary:hover {
          background: rgba(255,255,255,0.2);
          transform: translateY(-2px);
        }
        .btn-outline {
          background: transparent;
          color: var(--text-light);
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          border: 1px solid var(--text-gray);
          cursor: pointer;
          width: 100%;
          transition: all 0.2s;
        }
        .btn-outline:hover {
          border-color: var(--text-light);
          background: rgba(255,255,255,0.05);
        }
        .btn-text {
          background: none;
          border: none;
          color: var(--text-light);
          font-weight: 600;
          cursor: pointer;
        }
        .btn-white {
          background: white;
          color: var(--primary);
          padding: 1rem 2rem;
          border-radius: 8px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          font-size: 1.1rem;
          transition: all 0.2s;
        }
        .btn-white:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(255,255,255,0.2);
        }
        .btn-lg {
          padding: 1rem 2rem;
          font-size: 1.1rem;
        }

        /* Navigation */
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background: rgba(15, 23, 42, 0.85);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .nav-container {
          max-width: var(--max-width);
          margin: 0 auto;
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .nav-logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-weight: 700;
          font-size: 1.4rem;
        }
        .logo-icon {
          width: 36px;
          height: 36px;
          background: var(--gradient-main);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 800;
        }
        .logo-img {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          object-fit: contain;
        }
        .nav-links {
          display: flex;
          gap: 2.5rem;
        }
        .nav-links button {
          background: none;
          border: none;
          color: var(--text-gray);
          font-weight: 500;
          cursor: pointer;
          transition: color 0.2s;
          font-size: 0.95rem;
        }
        .nav-links button:hover {
          color: var(--text-light);
        }
        .nav-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }
        .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          color: white;
          cursor: pointer;
        }
        .mobile-menu-btn svg {
          width: 24px;
          height: 24px;
        }

        /* Hero */
        .hero-section {
          padding: 10rem 2rem 6rem;
          max-width: var(--max-width);
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1.1fr;
          gap: 5rem;
          align-items: center;
        }
        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(99, 102, 241, 0.1);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 30px;
          font-size: 0.9rem;
          color: var(--primary);
          margin-bottom: 2rem;
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); }
          50% { box-shadow: 0 0 0 10px rgba(99, 102, 241, 0); }
        }
        .hero-badge span {
          font-weight: 700;
          background: var(--primary);
          color: white;
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
        }
        .hero-title {
          font-size: 4.5rem;
          line-height: 1.05;
          margin-bottom: 1.5rem;
          font-weight: 800;
          letter-spacing: -0.02em;
        }
        .gradient-text {
          background: var(--gradient-main);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hero-subtitle {
          font-size: 1.35rem;
          color: var(--text-gray);
          margin-bottom: 2.5rem;
          line-height: 1.7;
          max-width: 540px;
        }
        .hero-cta {
          display: flex;
          gap: 1rem;
          margin-bottom: 3rem;
        }
        .icon-sm {
          width: 20px;
          height: 20px;
        }
        .hero-trust p {
          font-size: 0.9rem;
          color: var(--text-gray);
          margin-bottom: 1rem;
        }
        .trust-logos {
          display: flex;
          gap: 2rem;
          opacity: 0.5;
          font-weight: 600;
          font-size: 1.1rem;
        }
        
        /* Hero Visual */
        .hero-visual {
          position: relative;
        }
        .dashboard-preview {
          background: var(--dark-card);
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.1);
          padding: 1rem;
          box-shadow: 0 20px 50px rgba(0,0,0,0.5);
          position: relative;
          aspect-ratio: 16/10;
        }
        .preview-header {
          display: flex;
          margin-bottom: 1rem;
        }
        .preview-dots {
          display: flex;
          gap: 6px;
        }
        .preview-dots span {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
        }
        .preview-body {
          display: flex;
          gap: 1rem;
          height: calc(100% - 2rem);
        }
        .preview-sidebar {
          width: 20%;
          background: rgba(255,255,255,0.05);
          border-radius: 6px;
        }
        .preview-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .preview-chart {
          flex: 2;
          background: linear-gradient(180deg, rgba(99, 102, 241, 0.2) 0%, rgba(99, 102, 241, 0) 100%);
          border-radius: 6px;
          border-top: 2px solid var(--primary);
        }
        .preview-grid {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }
        .preview-grid div {
          background: rgba(255,255,255,0.05);
          border-radius: 6px;
        }
        .float-card {
          position: absolute;
          background: var(--dark-card);
          border: 1px solid rgba(255,255,255,0.1);
          padding: 0.75rem 1rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          animation: float 6s ease-in-out infinite;
        }
        .card-1 {
          top: 10%;
          right: -20px;
        }
        .card-2 {
          bottom: 10%;
          left: -20px;
          animation-delay: 2s;
        }
        .float-icon {
          width: 36px;
          height: 36px;
          background: rgba(99, 102, 241, 0.2);
          color: var(--primary);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .float-icon svg {
          width: 20px;
          height: 20px;
        }
        .float-text {
          display: flex;
          flex-direction: column;
          font-size: 0.75rem;
        }
        .float-text strong {
          font-size: 1rem;
          color: var(--text-light);
        }

        /* Video Play Button */
        .video-play-btn {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 10;
          background: rgba(99, 102, 241, 0.9);
          border: none;
          border-radius: 50px;
          padding: 1rem 2rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          backdrop-filter: blur(10px);
        }
        .video-play-btn:hover {
          background: var(--primary);
          transform: translate(-50%, -50%) scale(1.05);
        }
        .video-play-btn svg {
          width: 28px;
          height: 28px;
        }

        /* Stats Bar */
        .stats-bar {
          background: var(--gradient-subtle);
          border-top: 1px solid rgba(255,255,255,0.05);
          border-bottom: 1px solid rgba(255,255,255,0.05);
          padding: 3rem 2rem;
        }
        .stats-container {
          max-width: var(--max-width);
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 3rem;
        }
        .stat-item {
          text-align: center;
        }
        .stat-icon {
          width: 48px;
          height: 48px;
          margin: 0 auto 1rem;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
        }
        .stat-icon svg {
          width: 24px;
          height: 24px;
        }
        .stat-value {
          font-size: 2.5rem;
          font-weight: 800;
          background: var(--gradient-main);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .stat-label {
          color: var(--text-gray);
          font-size: 1rem;
          margin-top: 0.25rem;
        }

        /* Problem/Solution */
        .problem-section {
          padding: 8rem 2rem;
          background: rgba(255,255,255,0.02);
        }
        .section-container {
          max-width: var(--max-width);
          margin: 0 auto;
        }
        .problem-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6rem;
        }
        .section-title {
          font-size: 3rem;
          margin-bottom: 1.5rem;
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .text-center {
          text-align: center;
        }
        .section-desc {
          font-size: 1.15rem;
          color: var(--text-gray);
          margin-bottom: 2rem;
          line-height: 1.7;
        }
        .problem-list li, .solution-list li {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.25rem;
          font-size: 1.1rem;
        }
        .problem-title {
          color: #ef4444;
        }
        .problem-list svg {
          width: 24px;
          height: 24px;
          color: #ef4444;
          flex-shrink: 0;
        }
        .solution-list svg {
          width: 24px;
          height: 24px;
          color: var(--success);
          flex-shrink: 0;
        }

        /* Features */
        .features-section {
          padding: 8rem 2rem;
          max-width: var(--max-width);
          margin: 0 auto;
        }
        .section-header {
          text-align: center;
          max-width: 800px;
          margin: 0 auto 5rem;
        }
        .section-subtitle {
          font-size: 1.25rem;
          color: var(--text-gray);
          margin-top: 1rem;
        }
        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }
        .feature-card {
          background: var(--dark-card);
          padding: 2.5rem;
          border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.05);
          transition: all 0.3s;
        }
        .feature-card:hover {
          transform: translateY(-8px);
          border-color: var(--primary);
          background: var(--dark-card-hover);
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        .feature-icon {
          width: 56px;
          height: 56px;
          background: rgba(99, 102, 241, 0.1);
          color: var(--primary);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
        }
        .feature-icon svg {
          width: 28px;
          height: 28px;
        }
        .feature-card h3 {
          font-size: 1.35rem;
          margin-bottom: 0.75rem;
        }
        .feature-card p {
          color: var(--text-gray);
          line-height: 1.7;
        }

        /* Testimonials */
        .testimonials-section {
          padding: 8rem 2rem;
          background: rgba(255,255,255,0.02);
        }
        .testimonials-grid {
          max-width: var(--max-width);
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }
        .testimonial-card {
          background: var(--dark-card);
          padding: 2.5rem;
          border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.05);
          transition: all 0.3s;
        }
        .testimonial-card:hover {
          border-color: rgba(99, 102, 241, 0.3);
          transform: translateY(-5px);
        }
        .testimonial-stars {
          display: flex;
          gap: 0.25rem;
          margin-bottom: 1.5rem;
        }
        .testimonial-stars svg {
          width: 20px;
          height: 20px;
          color: #fbbf24;
        }
        .testimonial-quote {
          font-size: 1.1rem;
          line-height: 1.7;
          margin-bottom: 2rem;
          color: var(--text-light);
        }
        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .author-avatar {
          width: 48px;
          height: 48px;
          background: var(--gradient-main);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.9rem;
        }
        .author-info {
          display: flex;
          flex-direction: column;
        }
        .author-info strong {
          font-size: 1rem;
        }
        .author-info span {
          font-size: 0.875rem;
          color: var(--text-gray);
        }

        /* Comparison */
        .comparison-section {
          padding: 8rem 2rem;
        }
        .comparison-table-wrapper {
          max-width: 1000px;
          margin: 0 auto;
          overflow-x: auto;
        }
        .comparison-table {
          width: 100%;
          border-collapse: collapse;
          background: var(--dark-card);
          border-radius: 16px;
          overflow: hidden;
        }
        .comparison-table th,
        .comparison-table td {
          padding: 1.25rem 1.5rem;
          text-align: center;
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        .comparison-table th {
          background: rgba(255,255,255,0.02);
          font-weight: 600;
          font-size: 1rem;
        }
        .comparison-table td:first-child,
        .comparison-table th:first-child {
          text-align: left;
          font-weight: 500;
        }
        .comparison-table .highlight {
          background: rgba(99, 102, 241, 0.1);
        }
        .comparison-table th.highlight {
          color: var(--primary);
          background: rgba(99, 102, 241, 0.15);
        }
        .comparison-table .check {
          width: 24px;
          height: 24px;
          color: var(--success);
        }
        .comparison-table .cross {
          width: 24px;
          height: 24px;
          color: var(--text-gray);
          opacity: 0.5;
        }
        .comparison-table .partial {
          color: var(--warning);
          font-size: 0.875rem;
        }

        /* Steps */
        .steps-section {
          padding: 8rem 2rem;
          background: rgba(255,255,255,0.02);
        }
        .steps-container {
          max-width: var(--max-width);
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 2rem;
        }
        .step-item {
          flex: 1;
          text-align: center;
          position: relative;
        }
        .step-number {
          font-size: 5rem;
          font-weight: 800;
          color: rgba(255,255,255,0.03);
          margin-bottom: -2.5rem;
          position: relative;
          z-index: 0;
        }
        .step-item h3 {
          font-size: 1.5rem;
          margin-bottom: 1rem;
          position: relative;
          z-index: 1;
        }
        .step-item p {
          color: var(--text-gray);
          line-height: 1.6;
        }
        .step-connector {
          flex: 0.5;
          height: 2px;
          background: linear-gradient(90deg, rgba(99, 102, 241, 0.5) 0%, rgba(236, 72, 153, 0.5) 100%);
          margin-top: 3rem;
        }

        /* Agency Section */
        .agency-section {
          padding: 6rem 2rem;
          background: linear-gradient(180deg, rgba(99, 102, 241, 0.08) 0%, transparent 100%);
        }
        .agency-container {
          max-width: var(--max-width);
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }
        .agency-content {
          display: flex;
          flex-direction: column;
        }
        .agency-badge {
          display: inline-block;
          padding: 0.5rem 1rem;
          background: rgba(99, 102, 241, 0.2);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--primary);
          margin-bottom: 1.5rem;
          width: fit-content;
        }
        .agency-section .section-container {
          max-width: var(--max-width);
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }
        .agency-text h2 {
          font-size: 2.75rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
        }
        .agency-text p {
          font-size: 1.15rem;
          color: var(--text-gray);
          margin-bottom: 2rem;
          line-height: 1.7;
        }
        .agency-features {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .agency-feature {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 12px;
          border: 1px solid rgba(99, 102, 241, 0.2);
        }
        .agency-feature svg {
          width: 24px;
          height: 24px;
          color: var(--primary);
          flex-shrink: 0;
          margin-top: 2px;
        }
        .agency-feature h4 {
          font-size: 1rem;
          margin-bottom: 0.25rem;
        }
        .agency-feature p {
          font-size: 0.9rem;
          color: var(--text-gray);
          margin: 0;
        }
        .agency-visual {
          background: var(--dark-card);
          border-radius: 20px;
          padding: 2rem;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .company-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }
        .company-card {
          background: rgba(255,255,255,0.05);
          border-radius: 12px;
          padding: 1.25rem;
          text-align: center;
          border: 1px solid rgba(255,255,255,0.1);
          transition: all 0.3s;
        }
        .company-card:hover {
          border-color: var(--primary);
          transform: translateY(-3px);
        }
        .company-card h5 {
          font-size: 0.85rem;
          margin-bottom: 0.25rem;
        }
        .company-card span {
          font-size: 0.75rem;
          color: var(--text-gray);
        }
        .agency-stats {
          display: flex;
          justify-content: space-around;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .agency-stats .stat {
          text-align: center;
        }
        .agency-stats .stat-number {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--primary);
        }
        .agency-stats .stat-label {
          font-size: 0.85rem;
          color: var(--text-gray);
        }
        .agency-feature > div {
          display: flex;
          flex-direction: column;
        }
        .agency-feature strong {
          font-size: 1rem;
          margin-bottom: 0.25rem;
        }
        .agency-feature span {
          font-size: 0.9rem;
          color: var(--text-gray);
        }
        .agency-diagram {
          background: var(--dark-card);
          border-radius: 20px;
          padding: 2rem;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .diagram-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          background: var(--gradient-main);
          border-radius: 12px;
          margin-bottom: 1.5rem;
          font-weight: 600;
        }
        .diagram-icon {
          font-size: 1.5rem;
        }
        .diagram-branches {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding-left: 1.5rem;
        }
        .diagram-branch {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .branch-line {
          width: 30px;
          height: 2px;
          background: rgba(255,255,255,0.2);
        }
        .branch-company {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.25rem;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          flex: 1;
        }
        .branch-company.more {
          border-style: dashed;
          color: var(--text-gray);
        }
        .company-icon {
          font-size: 1.25rem;
        }
        .company-stats {
          margin-left: auto;
          font-size: 0.75rem;
          color: var(--text-gray);
        }

        /* Pricing */
        .pricing-section {
          padding: 8rem 2rem;
          max-width: var(--max-width);
          margin: 0 auto;
        }
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
          align-items: stretch;
        }
        .pricing-card {
          background: var(--dark-card);
          border-radius: 20px;
          padding: 2.5rem;
          border: 1px solid rgba(255,255,255,0.05);
          position: relative;
          display: flex;
          flex-direction: column;
          transition: all 0.3s;
        }
        .pricing-card:hover {
          border-color: rgba(99, 102, 241, 0.3);
          transform: translateY(-5px);
        }
        .pricing-card.popular {
          border-color: var(--primary);
          background: linear-gradient(180deg, rgba(99, 102, 241, 0.15) 0%, var(--dark-card) 100%);
          transform: scale(1.02);
          z-index: 1;
        }
        .pricing-card.popular:hover {
          transform: scale(1.02) translateY(-5px);
        }
        .popular-badge {
          position: absolute;
          top: -14px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--gradient-main);
          color: white;
          padding: 0.35rem 1.25rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .card-header {
          text-align: center;
          margin-bottom: 2rem;
          padding-bottom: 2rem;
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }
        .card-header h3 {
          font-size: 1.35rem;
          margin-bottom: 0.5rem;
        }
        .price {
          font-size: 3.5rem;
          font-weight: 800;
          margin: 1rem 0;
        }
        .price span {
          font-size: 1rem;
          color: var(--text-gray);
          font-weight: 400;
        }
        .card-features {
          flex: 1;
        }
        .card-features ul {
          list-style: none;
          padding: 0;
          margin: 0 0 2rem;
        }
        .card-features li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
          font-size: 0.95rem;
        }
        .card-features li svg {
          width: 20px;
          height: 20px;
          color: var(--primary);
          flex-shrink: 0;
        }
        .card-features li.disabled {
          color: var(--text-gray);
          opacity: 0.5;
        }
        .card-features li.disabled svg {
          color: var(--text-gray);
        }
        .feature-group {
          margin-bottom: 1.5rem;
        }
        .feature-group-title {
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--primary);
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid rgba(99, 102, 241, 0.2);
        }
        .feature-group ul {
          margin-bottom: 0;
        }
        .feature-group li {
          font-size: 0.85rem;
          margin-bottom: 0.6rem;
        }
        .feature-group li svg {
          width: 16px;
          height: 16px;
        }
        .pricing-card button {
          width: 100%;
          margin-top: auto;
        }

        /* FAQ Accordion */
        .faq-section {
          padding: 8rem 2rem;
          background: rgba(255,255,255,0.02);
        }
        .faq-accordion {
          max-width: 900px;
          margin: 3rem auto 0;
        }
        .faq-item {
          background: var(--dark-card);
          border-radius: 12px;
          margin-bottom: 1rem;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.05);
        }
        .faq-question {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          background: none;
          border: none;
          color: var(--text-light);
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          text-align: left;
          font-family: inherit;
        }
        .faq-toggle {
          font-size: 1.5rem;
          color: var(--primary);
          font-weight: 300;
        }
        .faq-answer {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }
        .faq-item.active .faq-answer {
          max-height: 300px;
        }
        .faq-answer p {
          padding: 0 1.5rem 1.5rem;
          color: var(--text-gray);
          line-height: 1.7;
        }

        /* Feature Matrix */
        .feature-matrix {
          margin-top: 5rem;
          padding-top: 3rem;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .matrix-title {
          text-align: center;
          font-size: 1.75rem;
          margin-bottom: 2rem;
          color: var(--text-light);
        }
        .matrix-wrapper {
          overflow-x: auto;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .matrix-table {
          width: 100%;
          border-collapse: collapse;
          background: var(--dark-card);
          min-width: 800px;
        }
        .matrix-table th,
        .matrix-table td {
          padding: 1rem 1.25rem;
          text-align: center;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          font-size: 0.9rem;
        }
        .matrix-table th {
          background: rgba(255,255,255,0.03);
          font-weight: 600;
          font-size: 1rem;
          position: sticky;
          top: 0;
        }
        .matrix-table td:first-child,
        .matrix-table th:first-child {
          text-align: left;
          font-weight: 500;
          padding-left: 1.5rem;
        }
        .matrix-table .feature-col {
          min-width: 200px;
        }
        .matrix-table .highlight {
          background: rgba(99, 102, 241, 0.08);
        }
        .matrix-table th.highlight {
          color: var(--primary);
          background: rgba(99, 102, 241, 0.15);
        }
        .matrix-table .check {
          width: 20px;
          height: 20px;
          color: var(--success);
        }
        .matrix-table .cross {
          width: 20px;
          height: 20px;
          color: var(--text-gray);
          opacity: 0.4;
        }
        .matrix-table tbody tr:hover {
          background: rgba(255,255,255,0.02);
        }
        .category-row {
          background: rgba(99, 102, 241, 0.1) !important;
        }
        .category-row td {
          font-weight: 700;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--primary);
          padding: 0.75rem 1.5rem;
        }

        /* Guarantee */
        .guarantee-section {
          padding: 6rem 2rem;
          text-align: center;
        }
        .guarantee-content {
          max-width: 700px;
          margin: 0 auto;
        }
        .guarantee-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 2rem;
          background: rgba(16, 185, 129, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--success);
        }
        .guarantee-icon svg {
          width: 40px;
          height: 40px;
        }
        .guarantee-content h2 {
          font-size: 2rem;
          margin-bottom: 1rem;
        }
        .guarantee-content p {
          color: var(--text-gray);
          font-size: 1.1rem;
          line-height: 1.7;
        }

        /* CTA */
        .cta-section {
          padding: 8rem 2rem;
          text-align: center;
          background: var(--gradient-main);
        }
        .cta-content h2 {
          font-size: 3rem;
          margin-bottom: 1rem;
        }
        .cta-content p {
          font-size: 1.35rem;
          margin-bottom: 2.5rem;
          opacity: 0.9;
        }
        .micro-copy {
          font-size: 0.95rem;
          margin-top: 1.25rem;
          opacity: 0.8;
        }

        /* Footer */
        .footer {
          padding: 5rem 2rem 2rem;
          background: var(--dark-bg);
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .footer-container {
          max-width: var(--max-width);
          margin: 0 auto;
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 4rem;
          margin-bottom: 4rem;
        }
        .footer-col h4 {
          margin-bottom: 1.5rem;
        }
        .footer-col p {
          color: var(--text-gray);
          line-height: 1.6;
          margin-top: 1rem;
        }
        .footer-col button {
          display: block;
          background: none;
          border: none;
          color: var(--text-gray);
          text-align: left;
          padding: 0;
          margin-bottom: 0.75rem;
          font-size: 1rem;
          cursor: pointer;
          transition: color 0.2s;
          font-family: inherit;
        }
        .footer-col button:hover {
          color: var(--text-light);
        }
        .footer-bottom {
          text-align: center;
          color: var(--text-gray);
          font-size: 0.9rem;
          padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.05);
        }

        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .features-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .pricing-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .testimonials-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .agency-section .section-container {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
          .agency-container {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
          .company-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .hero-section {
            grid-template-columns: 1fr;
            padding-top: 7rem;
            text-align: center;
            gap: 3rem;
          }
          .hero-title {
            font-size: 2.75rem;
          }
          .hero-subtitle {
            margin: 0 auto 2rem;
          }
          .hero-cta {
            justify-content: center;
            flex-wrap: wrap;
          }
          .trust-logos {
            justify-content: center;
            flex-wrap: wrap;
          }
          .stats-container {
            grid-template-columns: repeat(2, 1fr);
          }
          .problem-grid, .footer-container {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
          .steps-container {
            flex-direction: column;
          }
          .step-connector {
            display: none;
          }
          .features-grid,
          .testimonials-grid,
          .pricing-grid {
            grid-template-columns: 1fr;
          }
          .pricing-card.popular {
            transform: none;
          }
          .comparison-table {
            font-size: 0.85rem;
          }
          .comparison-table th,
          .comparison-table td {
            padding: 1rem;
          }
          .desktop-only {
            display: none;
          }
          .mobile-menu-btn {
            display: block;
          }
          .mobile-menu {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: var(--dark-card);
            padding: 1rem;
            display: flex;
            flex-direction: column;
            gap: 1rem;
            border-bottom: 1px solid rgba(255,255,255,0.1);
          }
          .mobile-menu button {
            background: none;
            border: none;
            color: var(--text-light);
            text-align: left;
            padding: 0.5rem;
            font-size: 1rem;
          }
          .mobile-actions {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid rgba(255,255,255,0.1);
          }
          .section-title {
            font-size: 2rem;
          }
          .cta-content h2 {
            font-size: 2rem;
          }
          .agency-text h2 {
            font-size: 2rem;
          }
          .agency-container {
            gap: 2rem;
          }
          .company-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .diagram-branches {
            padding-left: 0.5rem;
          }
          .branch-company {
            flex-wrap: wrap;
          }
          .company-stats {
            margin-left: 0;
            width: 100%;
            margin-top: 0.25rem;
          }
          .matrix-table {
            font-size: 0.8rem;
          }
          .matrix-table th,
          .matrix-table td {
            padding: 0.75rem 0.5rem;
          }
        }

        @media (max-width: 480px) {
          .stats-container {
            grid-template-columns: 1fr;
          }
          .hero-title {
            font-size: 2.25rem;
          }
          .stat-value {
            font-size: 2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
